import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config/api";

interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  description: string;
  planType: string;
  billingCycle: string;
  paymentDate: string;
  stripeInvoiceId?: string;
  stripePaymentIntentId?: string;
  failureReason?: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string;
    avatar?: string;
  };
  subscription?: {
    id: string;
    planType: string;
    billingCycle: string;
    status: string;
  };
}

interface PaymentStats {
  totals: {
    revenue: number;
    paymentCount: number;
    failedPayments: number;
  };
  revenueByPlan: Array<{
    planType: string;
    _sum: { amount: number };
    _count: { id: number };
  }>;
  statusBreakdown: Array<{
    status: string;
    _count: { id: number };
  }>;
  monthlyRevenue: Array<{
    month: string;
    total_amount: string;
    payment_count: string;
  }>;
  timeRange: string;
}

// Helper function to safely get numeric values
const getSafeNumber = (value: any, fallback: number = 0): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return parseInt(value) || fallback;
  if (typeof value === "bigint") return Number(value);
  return fallback;
};

// Helper function to safely render any value as string
const safeString = (value: any): string => {
  if (value === null || value === undefined) return "0";
  if (typeof value === "string") return value;
  if (typeof value === "number") return value.toString();
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "object") return "0"; // Don't render objects
  return String(value);
};

const AdminPaymentHistory: React.FC = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Filters
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [planTypeFilter, setPlanTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);

  // Auto-refresh functionality
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  const loadPaymentHistory = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) setLoading(true);
        const token = localStorage.getItem("adminToken");
        if (!token) {
          navigate("/admin/login");
          return;
        }

        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: "20",
        });

        if (searchQuery) params.append("search", searchQuery);
        if (statusFilter !== "all") params.append("status", statusFilter);
        if (planTypeFilter !== "all") params.append("planType", planTypeFilter);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        const response = await fetch(`${API_BASE_URL}/admin/payments?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setPayments(data.data?.payments || []);
          setTotalPages(data.data?.pagination?.totalPages || 1);
          setLastRefresh(new Date());
          setError("");
        } else {
          setError(data.error || "Failed to load payment history");
        }
      } catch (error) {
        console.error("Failed to load payment history:", error);
        if (showLoader) {
          setError("Network error. Please try again.");
        }
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [currentPage, statusFilter, planTypeFilter, startDate, endDate, searchQuery, navigate]
  );

  const loadPaymentStats = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setStatsLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/admin/payments/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.warn(`Stats API error: ${response.status}`);
        return; // Don't throw, just skip stats
      }

      const data = await response.json();
      console.log("Raw stats response:", data);

      if (data.success && data.data) {
        // 🔧 ULTRA SAFE data processing
        const safeStats: PaymentStats = {
          totals: {
            revenue: getSafeNumber(data.data.totals?.revenue, 0),
            paymentCount: getSafeNumber(data.data.totals?.paymentCount, 0),
            failedPayments: getSafeNumber(data.data.totals?.failedPayments, 0),
          },
          revenueByPlan: Array.isArray(data.data.revenueByPlan) 
            ? data.data.revenueByPlan.map((plan: any) => ({
                planType: safeString(plan.planType),
                _sum: { amount: getSafeNumber(plan._sum?.amount, 0) },
                _count: { id: getSafeNumber(plan._count?.id, 0) },
              }))
            : [],
          statusBreakdown: Array.isArray(data.data.statusBreakdown)
            ? data.data.statusBreakdown.map((status: any) => ({
                status: safeString(status.status),
                _count: { id: getSafeNumber(status._count?.id, 0) },
              }))
            : [],
          monthlyRevenue: Array.isArray(data.data.monthlyRevenue)
            ? data.data.monthlyRevenue.map((month: any) => ({
                month: safeString(month.month),
                total_amount: safeString(month.total_amount),
                payment_count: safeString(month.payment_count),
              }))
            : [],
          timeRange: safeString(data.data.timeRange || "30d"),
        };

        console.log("Processed safe stats:", safeStats);
        setStats(safeStats);
      } else {
        console.warn("Invalid stats response:", data);
      }
    } catch (error) {
      console.error("Failed to load payment stats:", error);
      // Don't set error for stats failure
    } finally {
      if (showLoader) setStatsLoading(false);
    }
  }, []);

  // Manual refresh function
  const handleManualRefresh = async () => {
    setError("");
    await Promise.all([loadPaymentHistory(false), loadPaymentStats(false)]);
  };

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        handleManualRefresh();
      }, 30000);
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [autoRefresh]);

  // Initial load and filter changes
  useEffect(() => {
    loadPaymentHistory();
  }, [loadPaymentHistory]);

  useEffect(() => {
    loadPaymentStats();
  }, [loadPaymentStats]);

  // Listen for subscription updates from other admin components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "admin-subscription-updated") {
        setTimeout(handleManualRefresh, 1000);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Custom event listener for same-tab updates
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      setTimeout(handleManualRefresh, 1000);
    };

    window.addEventListener("subscription-updated", handleSubscriptionUpdate);
    return () => window.removeEventListener("subscription-updated", handleSubscriptionUpdate);
  }, []);

  const formatAmount = (amount: number, currency: string = "usd") => {
    const safeAmount = getSafeNumber(amount, 0);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(safeAmount / 100);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      SUCCEEDED: "bg-green-100 text-green-800",
      PENDING: "bg-yellow-100 text-yellow-800",
      PROCESSING: "bg-blue-100 text-blue-800",
      FAILED: "bg-red-100 text-red-800",
      CANCELED: "bg-gray-100 text-gray-800",
      REFUNDED: "bg-purple-100 text-purple-800",
      PARTIALLY_REFUNDED: "bg-purple-100 text-purple-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusStyles[status as keyof typeof statusStyles] || "bg-gray-100 text-gray-800"
        }`}
      >
        {safeString(status)}
      </span>
    );
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setPlanTypeFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const viewPaymentDetails = (payment: PaymentRecord) => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
  };

  const viewUserDetails = (userId: string) => {
    navigate(`/admin/users/${userId}`);
  };

  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Payment History</h1>
              <p className="mt-1 text-sm text-gray-500">
                View and manage all user payments
                {lastRefresh && (
                  <span className="ml-2">
                    • Last updated: {formatDate(lastRefresh.toISOString())}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Auto-refresh toggle */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Auto-refresh:</label>
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                    autoRefresh ? "bg-red-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoRefresh ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {/* Manual refresh button */}
              <button
                onClick={handleManualRefresh}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  ></path>
                </svg>
                <span>Refresh</span>
              </button>

              <button
                onClick={() => navigate("/admin/subscriptions")}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Subscriptions
              </button>
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center justify-between">
            <span>{safeString(error)}</span>
            <button onClick={() => setError("")} className="text-red-500 hover:text-red-700">
              ✕
            </button>
          </div>
        )}

        {/* Payment Statistics - ULTRA SAFE VERSION */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Revenue</h3>
              <p className="text-3xl font-bold text-green-600">
                {formatAmount(getSafeNumber(stats.totals?.revenue, 0))}
              </p>
              {/* <p className="text-sm text-gray-500">
                {getSafeNumber(stats.totals?.paymentCount, 0)} successful payments
              </p> */}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed Payments</h3>
              <p className="text-3xl font-bold text-red-600">
                {getSafeNumber(stats.totals?.failedPayments, 0)}
              </p>
              <p className="text-sm text-gray-500">Requires attention</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Business Plan</h3>
              <p className="text-3xl font-bold text-blue-600">
                {formatAmount(
                  (stats.revenueByPlan || [])
                    .filter((p) => safeString(p.planType) === "BUSINESS")
                    .reduce((sum, p) => sum + getSafeNumber(p._sum?.amount, 0), 0)
                )}
              </p>
              <p className="text-sm text-gray-500">
                {(stats.revenueByPlan || [])
                  .filter((p) => safeString(p.planType) === "BUSINESS")
                  .reduce((sum, p) => sum + getSafeNumber(p._count?.id, 0), 0)}{" "}
                payments
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise Plan</h3>
              <p className="text-3xl font-bold text-purple-600">
                {formatAmount(
                  (stats.revenueByPlan || [])
                    .filter((p) => safeString(p.planType) === "ENTERPRISE")
                    .reduce((sum, p) => sum + getSafeNumber(p._sum?.amount, 0), 0)
                )}
              </p>
              <p className="text-sm text-gray-500">
                {(stats.revenueByPlan || [])
                  .filter((p) => safeString(p.planType) === "ENTERPRISE")
                  .reduce((sum, p) => sum + getSafeNumber(p._count?.id, 0), 0)}{" "}
                payments
              </p>
            </div>
          </div>
        )}

        {/* Show loading state for stats */}
        {statsLoading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Email, name, invoice ID..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="all">All Statuses</option>
                <option value="SUCCEEDED">Succeeded</option>
                <option value="PENDING">Pending</option>
                <option value="PROCESSING">Processing</option>
                <option value="FAILED">Failed</option>
                <option value="CANCELED">Canceled</option>
                <option value="REFUNDED">Refunded</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
              <select
                value={planTypeFilter}
                onChange={(e) => {
                  setPlanTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="all">All Plans</option>
                <option value="FREE">Free</option>
                <option value="BUSINESS">Business</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Payment History Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Payment Records
              {autoRefresh && <span className="ml-2 text-sm text-green-600">● Live</span>}
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No payments found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {payment.user.avatar ? (
                                <img className="h-10 w-10 rounded-full" src={payment.user.avatar} alt="" />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-gray-600 font-medium text-sm">
                                    {safeString(payment.user.name).charAt(0) || safeString(payment.user.email).charAt(0)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {safeString(payment.user.name) || "No name"}
                              </div>
                              <div className="text-sm text-gray-500">{safeString(payment.user.email)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatAmount(payment.amount, payment.currency)}
                          </div>
                          <div className="text-sm text-gray-500">{safeString(payment.paymentMethod)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{safeString(payment.planType) || "N/A"}</div>
                          <div className="text-sm text-gray-500">{safeString(payment.billingCycle) || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(payment.status)}
                          {payment.status === "FAILED" && payment.failureReason && (
                            <div className="text-xs text-red-600 mt-1">{safeString(payment.failureReason)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(payment.paymentDate || payment.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => viewPaymentDetails(payment)}
                            className="text-red-600 hover:text-red-900 mr-4"
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => viewUserDetails(payment.user.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            View User
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{currentPage}</span> of{" "}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ‹
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? "z-10 bg-red-50 border-red-500 text-red-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}

                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ›
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payment Details Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">User Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="text-sm font-medium">{safeString(selectedPayment.user.name) || "No name"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-sm font-medium">{safeString(selectedPayment.user.email)}</p>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Payment Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="text-sm font-medium">
                      {formatAmount(selectedPayment.amount, selectedPayment.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedPayment.status)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Plan</p>
                    <p className="text-sm font-medium">
                      {safeString(selectedPayment.planType)} - {safeString(selectedPayment.billingCycle)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Method</p>
                    <p className="text-sm font-medium capitalize">{safeString(selectedPayment.paymentMethod)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Date</p>
                    <p className="text-sm font-medium">{formatDate(selectedPayment.paymentDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Created At</p>
                    <p className="text-sm font-medium">{formatDate(selectedPayment.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Stripe Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Stripe Information</h4>
                <div className="grid grid-cols-1 gap-4">
                  {selectedPayment.stripeInvoiceId && (
                    <div>
                      <p className="text-sm text-gray-500">Invoice ID</p>
                      <p className="text-sm font-mono font-medium">{safeString(selectedPayment.stripeInvoiceId)}</p>
                    </div>
                  )}
                  {selectedPayment.stripePaymentIntentId && (
                    <div>
                      <p className="text-sm text-gray-500">Payment Intent ID</p>
                      <p className="text-sm font-mono font-medium">{safeString(selectedPayment.stripePaymentIntentId)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedPayment.description && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-sm text-gray-700">{safeString(selectedPayment.description)}</p>
                </div>
              )}

              {/* Failure Reason */}
              {selectedPayment.status === "FAILED" && selectedPayment.failureReason && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">Failure Reason</h4>
                  <p className="text-sm text-red-700">{safeString(selectedPayment.failureReason)}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => viewUserDetails(selectedPayment.user.id)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                View User Profile
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentHistory;